import React, { useState, useEffect, useCallback } from "react"
import { useStorage } from "@app/hooks/useCore"
import { useIPAPI } from "@app/hooks/useIPAPI"

import config from "@root/config.js"

type LocationContextProps = StoreLocation & {
  loading: boolean
}

type ContextProps = LocationContextProps & {
  stores: Store[]
  updateLocation: (countryCode: string, updateCountry?: boolean) => Promise<void>
}

export const LocationContext = React.createContext<LocationContextProps | undefined>(undefined)

/**
 * QA/testing override: `?country=US` forces the geolocated country, bypassing the
 * IP lookup and any cached value. Lets the US-only PDP callout (and other geo-gated
 * UI) be exercised on staging, which otherwise forces the AU region and only ever
 * resolves `country` from the visitor's real IP. Ignored unless it's a 2-letter code.
 */
const getCountryOverride = (): string => {
  if (typeof window === "undefined") return ""
  const param = new URLSearchParams(window.location.search).get("country")
  return param && /^[a-z]{2}$/i.test(param) ? param.toUpperCase() : ""
}

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getStorage, setStorage } = useStorage()
  const {
    store,
    stores,
    settings: { keys },
  } = config as unknown as Config
  const { fetchIpapiResponse } = useIPAPI()

  const selectedStore = stores[getStorage(keys.shopify)] || store
  const [loading, setLoading] = useState(false)

  // Override takes precedence over the cached country so QA can defeat a stale value.
  const initialCountry = getCountryOverride() || getStorage(keys.country)

  const [settings, setSettings] = useState({
    store,
    country: initialCountry || "",
    locations: Object.values(stores).map((store: any) => store.locationRegion),
    locating: !initialCountry?.length,
    visitor: !selectedStore.locationCountries?.includes(initialCountry),
  })

  const updateLocation = useCallback(
    async (countryCode: string, updateCountry = false) => {
      setLoading(true)
      const newStore: Store = Object.values(stores).find(store => store.locationCountries.includes(countryCode)) || selectedStore

      setSettings(prevState => ({
        ...prevState,
        store: selectedStore.locationForceRegion ? prevState.store : newStore,
        country: updateCountry ? countryCode : settings.country,
        locating: false,
        visitor: !selectedStore.locationForceRegion ? !newStore.locationCountries?.includes(settings.country) : false,
      }))
      setLoading(false)
    },
    [selectedStore, stores, settings]
  )

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      ...settings,
      stores,
      updateLocation,
      loading,
    }),
    [settings, updateLocation, loading, stores]
  )

  useEffect(() => {
    async function fetchLocation() {
      if (settings.store && settings.country) {
        setStorage(keys.country, settings.country)
        setStorage(keys.shopify, settings.store.shopifyShopDomain.split(".")[0])
      } else {
        try {
          const key = "country"
          const data = await fetchIpapiResponse(store.locationLookupUrl)
          if (data[key]) updateLocation(data[key] || selectedStore.locationRegion, true)
        } catch (e) {
          updateLocation(selectedStore.locationRegion, true)
          console.warn("Error locating country", (e as Error).message)
        }
      }
    }
    fetchLocation()
  }, [selectedStore, store, settings, keys, setStorage, updateLocation, fetchIpapiResponse])

  return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>
}

export const useLocationContext = (): ContextProps => ({ ...React.useContext(LocationContext) }) as ContextProps
